import { MarkedTypeEnum } from '@doltech/utils/lib/constants';
import cl from 'classnames';
import { noop } from 'lodash';
import * as React from 'react';
import { DropTargetMonitor, useDrop } from 'react-dnd';
import { useDebounce, useMount } from 'react-use';
import styled from 'styled-components';

import { deviceBreakpoints } from '../../hooks/useDeviceQuery.hook';
import { QuestionCircle, QuestionDisplayMode } from '../CircleText/CircleText';
import { colorsV2 } from '../colors-v2';
import { Typography } from '../DesignSystemV2/Typography';
import renderNode, { Div } from '../renderNode';
import { QuestionBoxWrapper } from './QuestionBoxWrapper';
import QuestionConnector from './QuestionConnector';
import { QuestionV2 } from './QuestionV2';
import { DoTestEditor } from '../DoTestEditor/DoTestEditor';
import { FontSizeElementVariants } from '../DolEditor/decorators/font-size-switcher/components/FontSizeSwitcherElement.types';
import { isJson } from '@doltech/utils/lib/object';

const Main = styled.div`
  position: relative;
  font-weight: 500;
  &:not(:last-child) {
    margin-bottom: 8px;
  }
  .question {
    display: flex;
    align-items: center;
    .question-no,
    .question-do-test {
      margin-right: 12px;
    }
  }

  .question-connector-dropable {
    display: grid;
    position: relative;
    grid-template-columns: 43px 1fr;
    gap: 8px;
    padding-left: 32px;
    margin-top: 8px;
  }

  .question-dropable {
    display: flex;
    align-items: center;
    @media ${deviceBreakpoints.desktop} {
      height: 50px;
    }
  }

  .question-dropable-list {
    padding-top: 20px;
    position: relative;
  }

  &.${MarkedTypeEnum.CORRECT} {
    .question .question-do-test {
      background-color: ${colorsV2.green100};
    }
  }
  &.${MarkedTypeEnum.WRONG} {
    .question .question-do-test {
      background-color: ${colorsV2.red100};
    }
  }
  &.${MarkedTypeEnum.MISSING} {
    .question .question-do-test {
      background-color: ${colorsV2.black5};
      > span {
        color: ${colorsV2.black100};
      }
    }
  }
`;

interface QuestionDropableProps {
  questionContent?: React.ReactNode;
  no: string | number;
  title: string | number;
  nodes: {
    color: string;
    renderElement: React.ReactElement | string;
  }[];
  markedStatus?: MarkedTypeEnum;

  canDrop?: boolean;
  isOver?: boolean;
  onOptionChange?: (value) => void;
}

export const QuestionDropable = (props: QuestionDropableProps) => {
  const { questionContent, no, nodes, title, markedStatus, canDrop, isOver, onOptionChange } =
    props;
  const questionDropableRefs = React.useRef({});
  const [nodeOffset, setNodeOffset] = React.useState([]);
  const [nodeCount, setNodeCount] = React.useState(0);
  const calculateLayout = React.useCallback(() => {
    if (questionDropableRefs.current) {
      setNodeOffset(
        nodes.map((_node, nodeIndex) => {
          return {
            y: questionDropableRefs.current?.[nodeIndex]?.offsetTop || 0,
            height: questionDropableRefs.current?.[nodeIndex]?.clientHeight || 0,
          };
        })
      );
    }
  }, [nodes]);

  const [, cancel] = useDebounce(
    () => {
      calculateLayout();
    },
    400,
    [nodeCount]
  );

  useMount(() => {
    calculateLayout();
  });

  return (
    <Main
      className={cl('question-dropable-container', markedStatus, {
        'can-drop': canDrop,
        'is-over': isOver,
      })}
    >
      {questionContent || (
        <div className="question">
          <QuestionV2 no={no} id={`question-no-${no}`} />
          <QuestionBox>
            <DoTestEditor
              initialValue={
                isJson(title as any)
                  ? JSON.parse(title as any)
                  : [
                      {
                        type: FontSizeElementVariants.PARAGRAPH,
                        children: [
                          {
                            text: title ?? '',
                          },
                        ],
                      },
                    ]
              }
              readOnly
              onChange={onOptionChange}
            />
          </QuestionBox>
        </div>
      )}

      <div className="question-connector-dropable">
        <div className="question-connector">
          <QuestionConnector
            nodes={nodes
              .map((node, nodeIndex) => {
                return {
                  color: node.color,
                  y: nodeOffset[nodeIndex]?.y,
                  height: nodeOffset[nodeIndex]?.height || 0,
                };
              })
              .filter((node) => node.height !== 0)}
          />
        </div>

        <div className="question-dropable-list">
          {nodes.map((node, nodeIndex) => {
            const { renderElement } = node;
            return (
              <div
                key={nodeIndex}
                ref={(c) => {
                  questionDropableRefs.current[nodeIndex] = c;
                  cancel();
                  setNodeCount(nodeIndex);
                }}
                className="question-dropable"
              >
                {renderNode(Div, renderElement, props)}
              </div>
            );
          })}
        </div>
      </div>
    </Main>
  );
};

interface QuestionBoxProps {
  children: React.ReactElement | string | React.ReactElement[];
  canDrop?: boolean;
  isOver?: boolean;
  dropable?: boolean;
  displayMode?: QuestionDisplayMode;
  onMouseEnter?: (e: any) => void;
  onMouseLeave?: (e: any) => void;
  isMatching?: boolean;
  className?: string;
  notAnswered?: boolean;
}

const QuestionBoxContainer = styled.div`
  &.is-over {
    background: red;
  }
`;

export const QuestionBox = React.forwardRef((props: QuestionBoxProps, ref: any) => {
  const {
    children,
    canDrop,
    isOver,
    dropable,
    displayMode,
    isMatching = false,
    onMouseEnter = noop,
    onMouseLeave = noop,
    className,
    notAnswered,
    ...rest
  } = props;
  return (
    <QuestionBoxContainer ref={ref} className={cl('question-box', displayMode)} {...rest}>
      <QuestionBoxWrapper
        isMatching={isMatching}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={cl('question-box-wrapper', displayMode, {
          dropable,
          'can-drop': canDrop,
          'is-drag-over': isOver,
          'not-answered': notAnswered,
        })}
      >
        {children}
      </QuestionBoxWrapper>
    </QuestionBoxContainer>
  );
});

interface DropableBoxProps {
  accept: string;
  /**
   * @param dragData drag data that you defined in drag component
   *
   * Optional.
   * Called when a compatible item is dropped on the target. You may either return undefined, or a plain object.
   * If you return an object, it is going to become the drop result and will be available to the drag source in its
   * endDrag method as monitor.getDropResult(). This is useful in case you want to perform different actions
   * depending on which target received the drop. If you have nested drop targets, you can test whether a nested
   * target has already handled drop by checking monitor.didDrop() and monitor.getDropResult(). Both this method and
   * the source's endDrag method are good places to fire Flux actions. This method will not be called if canDrop()
   * is defined and returns false.
   */
  onDrop?: (dragData: any, monitor: DropTargetMonitor) => any;
}

export const DropableBox = ({ accept, onDrop }: DropableBoxProps) => {
  const [{ canDrop, isOver }, drop] = useDrop({
    accept,
    drop: onDrop,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  return (
    <div ref={drop}>
      <QuestionBox notAnswered canDrop={canDrop} isOver={isOver} dropable>
        <div className="row empty-drop-box">
          <QuestionCircle className={QuestionDisplayMode.QUESTION_OPTION} />
          <Typography.Text variant="regular/14">Drop your answer here</Typography.Text>
        </div>
      </QuestionBox>
    </div>
  );
};

interface DroppableWrapper extends DropableBoxProps {
  children: ({ canDrop, isOver }: { canDrop: boolean; isOver: boolean }) => JSX.Element;
}

export const DropableWrapper = ({ accept, onDrop, children }: DroppableWrapper) => {
  const [{ canDrop, isOver }, drop] = useDrop({
    accept,
    drop: onDrop,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });
  return <div ref={drop}>{children({ canDrop, isOver })}</div>;
};
